import { useCallback, useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent, useWindowDimensions } from 'react-native';
import { Flex, RemoteImage } from '@banhala/ui-kit';
import type { VerticalBannerListProps, VerticalBannerItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isNil, convertDeeplinkToWebLink, getWebBaseURL } from '../../../../utils';
import { ABLY_INFO } from '../../../../constant/ablyInfo';

const ASPECT_RATIO = 16 / 9.25;
const HORIZONTAL_PADDING = 16;
const BORDER_RADIUS = 6;

interface VerticalBannerProps {
  item: VerticalBannerItem;
  logging: AnyObject;
  imageWidth: number;
  imageHeight: number;
}

const VerticalBanner = ({
  item,
  logging,
  imageWidth,
  imageHeight,
}: VerticalBannerProps) => {
  const { thumbnail, deeplink } = item;

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_VERTICAL_BANNER_LIST',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          const convertedLink = convertDeeplinkToWebLink(deeplink);
          if (convertedLink === ABLY_INFO.url) {
            location.href = ABLY_INFO.url;
            return;
          }
          location.href = `${baseURL}${convertedLink}`;
        },
      });
    }
  }, [deeplink, logging]);

  return (
    <Flex
      padding={{ horizontal: HORIZONTAL_PADDING }}
      onPress={handlePress}
    >
      <Flex
        borderRadius={BORDER_RADIUS}
        overflow="hidden"
      >
        <RemoteImage
          uri={thumbnail}
          width={imageWidth}
          height={imageHeight}
        />
      </Flex>
    </Flex>
  );
};

export const VerticalBannerList = ({ entity }: VerticalBannerListProps) => {
  const displayLoggedRef = useRef(false);
  const { width: screenWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(screenWidth);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    if (width > 0) {
      setContainerWidth(width);
    }
  }, []);

  const imageWidth = containerWidth - HORIZONTAL_PADDING * 2;
  const imageHeight = imageWidth / ASPECT_RATIO;

  useEffect(() => {
    if (displayLoggedRef.current || isNil(entity) || entity.length === 0) {
      return;
    }
    displayLoggedRef.current = true;
  }, [entity]);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <Flex gap={8} onLayout={handleLayout}>
      {entity.map((bannerData, index) => (
        <VerticalBanner
          key={index}
          item={bannerData.item}
          logging={bannerData.logging}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        />
      ))}
    </Flex>
  );
};
